// All requests go to the Worker directly — this admin app has no backend
// of its own. credentials: "include" is what makes the httpOnly session
// cookies actually get sent; without it, every request would look
// logged-out even right after a successful login.

const API_BASE = import.meta.env.VITE_API_BASE ?? "https://4fuapi.hitowns.com";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  // Multiple components can hit a 401 around the same time (several
  // widgets loading on the dashboard, for instance) — collapse concurrent
  // refresh attempts into one shared request rather than firing several
  // redundant /admin/refresh-session calls at once.
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API_BASE}/admin/refresh-session`, {
      method: "POST",
      credentials: "include",
    })
      .then((res) => res.ok)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  /** Extra query params appended to the URL. */
  params?: Record<string, string | number | undefined>;
  /** Skip the automatic refresh-and-retry-once behavior (used by the
   * refresh call itself and login, to avoid loops). */
  skipRefresh?: boolean;
}

function buildUrl(path: string, params?: RequestOptions["params"]): string {
  const url = new URL(path, API_BASE);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/**
 * The one function every page's data fetching goes through. On a 401, it
 * tries exactly one silent refresh-and-retry before giving up — this is
 * what makes a session outlive the ~1 hour access-token lifetime without
 * the user ever noticing, as long as their refresh cookie is still valid.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, params, skipRefresh = false } = options;

  const doFetch = () =>
    fetch(buildUrl(path, params), {
      method,
      credentials: "include",
      headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

  let response = await doFetch();

  if (response.status === 401 && !skipRefresh) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await doFetch();
    }
  }

  if (!response.ok) {
    let message = `request failed (${response.status})`;
    try {
      const data = await response.json();
      if (typeof data?.error === "string") message = data.error;
    } catch {
      // response wasn't JSON — keep the generic message above
    }
    throw new ApiError(message, response.status);
  }

  // 204 No Content (logout, some deletes) has no body to parse.
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}

/** Reads a PostgREST-style Content-Range response header ("0-19/142") for
 * pagination total counts, where the backend passes it through. */
export function parseTotalFromRange(header: string | null): number | null {
  if (!header) return null;
  const total = header.split("/")[1];
  if (!total || total === "*") return null;
  return parseInt(total, 10);
}

/** Round 54 — upload a raw image (agency picture) to the Worker, which
 * validates it (magic bytes, <= 2 MB) and stores it; returns the public URL. */
export async function uploadAgencyPicture(file: File): Promise<string> {
  const doFetch = () =>
    fetch(buildUrl("/admin/agencies/upload-picture"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
  let res = await doFetch();
  if (res.status === 401 && (await refreshSession())) res = await doFetch();
  if (!res.ok) {
    let message = `upload failed (${res.status})`;
    try { const d = await res.json(); if (typeof d?.error === "string") message = d.error; } catch { /* keep generic */ }
    throw new ApiError(message, res.status);
  }
  return (await res.json()).url as string;
}

/** Round 55 — upload a banner / reward image for a CP event (Worker validates type + size, returns the public URL). */
export async function uploadCpEventImage(file: File, kind: "banner" | "reward_image" = "banner"): Promise<string> {
  const doFetch = () =>
    fetch(buildUrl("/admin/cp-events/upload-image", { kind }), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
  let res = await doFetch();
  if (res.status === 401 && (await refreshSession())) res = await doFetch();
  if (!res.ok) {
    let message = `upload failed (${res.status})`;
    try { const d = await res.json(); if (typeof d?.error === "string") message = d.error; } catch { /* keep generic */ }
    throw new ApiError(message, res.status);
  }
  return (await res.json()).url as string;
}

/** Round 56 (6B) - the one admin image upload. The Worker validates the real file type, resizes/compresses per `kind`
 *  (gift_icon, medal, mall_item, avatar_frame, room_theme, banner, reward_image, agency_picture, generic), keeps PNG
 *  transparency, and passes GIF/SVGA/MP4/WebM through untouched. */
export interface AdminUploadResult { url: string; thumbUrl?: string | null; originalUrl?: string | null; optimized?: boolean; width?: number; height?: number }
export async function uploadAdminImage(file: File, kind: string): Promise<AdminUploadResult> {
  const doFetch = () =>
    fetch(buildUrl("/admin/media/upload", { kind }), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
  let res = await doFetch();
  if (res.status === 401 && (await refreshSession())) res = await doFetch();
  if (!res.ok) {
    let message = `upload failed (${res.status})`;
    try { const d = await res.json(); if (typeof d?.error === "string") message = d.error; } catch { /* keep generic */ }
    throw new ApiError(message, res.status);
  }
  return (await res.json()) as AdminUploadResult;
}
